import { COOKIE_DE_SOCIO, DIAS_DE_COOKIE, esCodigoDeSocio, normalizarCodigo, PARAMETRO_DE_SOCIO } from '@mc/socios'

/**
 * La cookie del enlace de un socio (CLAUDE.md §30). El enlace es
 * `https://wixy.app/es?ref=CODIGO`: la web comercial deja la cookie, y el panel
 * también, por si el invitado llega aquí con el `?ref=` puesto (un enlace directo
 * a «crear cuenta», por ejemplo). **Solo si la persona acepta** (decidido el 09-10-2026):
 * el panel enseña el aviso (`AvisoDeSocio`) y no escribe nada hasta el «Aceptar»; «No,
 * gracias» deja en el almacenamiento local solo el código rechazado. Cuando el invitado crea su cuenta y entra, el
 * panel se la enseña al núcleo una vez, y el núcleo decide si la cuenta queda
 * atribuida: aquí solo se transporta el código.
 *
 * Funciones puras sobre lo mínimo del navegador, para probarlas sin él.
 */
export type DocumentoConCookies = { cookie: string }

type OpcionesDeCookie = { hostname: string; https: boolean }

/** Solo bajo `wixy.app` la cookie vale para todos los subdominios; en local, sin `Domain`. */
export const dominioDeLaCookie = (hostname: string): null | string => {
  const h = hostname.toLowerCase()
  if (h === 'wixy.app' || h.endsWith('.wixy.app')) return '.wixy.app'
  return null
}

/** El código que trae una dirección, normalizado, o `null` si no lo trae o no es un código. */
export const codigoDeLaDireccion = (search: string): null | string => {
  const valor = new URLSearchParams(search).get(PARAMETRO_DE_SOCIO)
  if (!valor || !esCodigoDeSocio(valor)) return null
  return normalizarCodigo(valor)
}

/** La misma dirección sin el `?ref=`, para dejarla limpia en la barra. */
export const direccionSinCodigo = (pathname: string, search: string, hash = ''): string => {
  const p = new URLSearchParams(search)
  p.delete(PARAMETRO_DE_SOCIO)
  const resto = p.toString()
  return `${pathname}${resto ? `?${resto}` : ''}${hash}`
}

const atributos = ({ hostname, https }: OpcionesDeCookie): string[] => {
  const partes = ['Path=/', 'SameSite=Lax']
  const dominio = dominioDeLaCookie(hostname)
  if (dominio) partes.push(`Domain=${dominio}`)
  if (https) partes.push('Secure')
  return partes
}

/** El texto de `document.cookie =` que guarda el código durante `DIAS_DE_COOKIE`. */
export const cadenaDeCookie = (codigo: string, opciones: OpcionesDeCookie): string =>
  [`${COOKIE_DE_SOCIO}=${encodeURIComponent(normalizarCodigo(codigo))}`, `Max-Age=${DIAS_DE_COOKIE * 86_400}`, ...atributos(opciones)].join('; ')

/** El texto que la borra: mismo nombre, mismo `Path` y mismo `Domain`, o el navegador no la encuentra. */
export const cadenaDeBorrado = (opciones: OpcionesDeCookie): string => [`${COOKIE_DE_SOCIO}=`, 'Max-Age=0', ...atributos(opciones)].join('; ')

/** El código guardado en la cookie, o `null` si no hay o no es un código. */
export const leerCookie = (documento: DocumentoConCookies): null | string => {
  for (const trozo of documento.cookie.split(';')) {
    const [nombre, ...valor] = trozo.trim().split('=')
    if (nombre !== COOKIE_DE_SOCIO) continue
    let codigo = ''
    try {
      codigo = decodeURIComponent(valor.join('='))
    } catch {
      return null
    }
    return esCodigoDeSocio(codigo) ? normalizarCodigo(codigo) : null
  }
  return null
}

/** La clave de `sessionStorage` con la que se recuerda que ya se intentó atribuir este (espacio, código). */
export const claveDeIntento = (espacio: number, codigo: string): string => `wixy-socio-atribuido:${espacio}:${normalizarCodigo(codigo)}`

// --- Lo que toca el navegador ---------------------------------------------------------

const opcionesDelNavegador = (): OpcionesDeCookie => ({ hostname: window.location.hostname, https: window.location.protocol === 'https:' })

export const guardarCookie = (codigo: string) => {
  document.cookie = cadenaDeCookie(codigo, opcionesDelNavegador())
}

export const borrarCookie = () => {
  document.cookie = cadenaDeBorrado(opcionesDelNavegador())
}

export const cookieGuardada = (): null | string => leerCookie(document)

const RECHAZADO = 'wixy_socio_rechazado'

/** Si ya se dijo «No, gracias» a este código en este navegador. */
export const fueRechazado = (codigo: string): boolean => {
  try {
    return localStorage.getItem(RECHAZADO) === normalizarCodigo(codigo)
  } catch {
    return false
  }
}

/** «No, gracias»: se recuerda solo el código, para no volver a preguntar por el mismo enlace. */
export const rechazarCodigo = (codigo: string) => {
  try {
    localStorage.setItem(RECHAZADO, normalizarCodigo(codigo))
  } catch {
    /* sin almacenamiento, se volverá a preguntar: sin la cookie no pasa nada */
  }
}

/** «Aceptar»: ahora sí, la cookie. */
export const aceptarCodigo = (codigo: string) => {
  guardarCookie(codigo)
  try {
    localStorage.removeItem(RECHAZADO)
  } catch {
    /* nada que quitar */
  }
}

/**
 * Al arrancar el panel: si la dirección trae `?ref=`, se quita de la barra y se devuelve el
 * código **para preguntar**, salvo que ya esté guardado o que ya se rechazara. No escribe nada.
 */
export const codigoPorPreguntar = (): null | string => {
  const codigo = codigoDeLaDireccion(window.location.search)
  if (!codigo) return null
  window.history.replaceState(window.history.state, '', direccionSinCodigo(window.location.pathname, window.location.search, window.location.hash))
  if (cookieGuardada() === codigo || fueRechazado(codigo)) return null
  return codigo
}

/** Si ya se intentó con este espacio y este código en esta pestaña del navegador. */
export const yaIntentado = (espacio: number, codigo: string): boolean => {
  try {
    return sessionStorage.getItem(claveDeIntento(espacio, codigo)) === '1'
  } catch {
    return false
  }
}

export const anotarIntento = (espacio: number, codigo: string) => {
  try {
    sessionStorage.setItem(claveDeIntento(espacio, codigo), '1')
  } catch {
    /* sin almacenamiento, se intentará otra vez al recargar; el núcleo lo aguanta */
  }
}
